import type {
    DefaultMenuItem,
    GetContextMenuItems,
    GridApi,
    MenuItemDef,
} from 'ag-grid-community';
import { saveAs } from 'file-saver';
import { escapeCsvFormula, prependEnvToFilename } from 'helpers/exportHelpers';
import { escapeRegExp } from 'lodash';

type ContextMenuItemT = DefaultMenuItem | MenuItemDef;

// AG Grid wraps every CSV field in quotes and doubles the inner ones, so each
// field reads as "(?:[^"]|"")*" right after a separator or a line start
const getAgGridCsvFieldRegex = (separator: string): RegExp =>
    new RegExp(
        String.raw`(^|${escapeRegExp(separator)}|\r\n)"((?:[^"]|"")*)"`,
        'g',
    );

/**
 * Escape the formula-like fields of a CSV produced by AG Grid. Every other
 * byte is left untouched, so the file only differs on the escaped cells.
 */
export const escapeFormulasInAgGridCsv = (
    csv: string,
    separator: string,
): string =>
    csv.replace(
        getAgGridCsvFieldRegex(separator),
        (field: string, boundary: string, content: string) => {
            const value = content.replaceAll('""', '"');
            const escapedValue = escapeCsvFormula(value);
            if (escapedValue === value) return field;
            return `${boundary}"${escapedValue.replaceAll('"', '""')}"`;
        },
    );

/** Same file name rule as AG Grid's native CSV export. */
export const getCsvExportFileName = (api: GridApi): string => {
    const fileName = api.getGridOption('defaultCsvExportParams')?.fileName;
    const name =
        typeof fileName === 'function'
            ? fileName({ api, context: api.getGridOption('context') })
            : fileName;
    if (!name) return 'export.csv';
    return name.includes('.') ? name : `${name}.csv`;
};

const downloadFormulaSafeCsv = (api: GridApi): void => {
    const csv = api.getDataAsCsv();
    if (csv === undefined) return;
    const separator =
        api.getGridOption('defaultCsvExportParams')?.columnSeparator ?? ',';
    const formulaSafeCsv = escapeFormulasInAgGridCsv(csv, separator);
    // Same UTF-8 BOM and type as AG Grid's native export
    const blob = new Blob(['\ufeff', formulaSafeCsv], { type: 'text/plain' });
    // file-saver clicks a detached anchor, so the global filename interceptor never sees it
    saveAs(blob, prependEnvToFilename(getCsvExportFileName(api)));
};

const getAgGridIconHtml = (iconName: string): string =>
    `<span class="ag-icon ag-icon-${iconName}" role="presentation" unselectable="on"></span>`;

const getCsvExportMenuItem = (api: GridApi): MenuItemDef => ({
    name: api.getGridOption('localeText')?.csvExport ?? 'CSV Export',
    icon: getAgGridIconHtml('csv'),
    action: () => downloadFormulaSafeCsv(api),
});

// Rebuilds AG Grid's native "export" sub menu around the formula-safe CSV entry
const getExportMenuItem = (api: GridApi): MenuItemDef | null => {
    const subMenu: ContextMenuItemT[] = [];
    if (!api.getGridOption('suppressCsvExport'))
        subMenu.push(getCsvExportMenuItem(api));
    if (!api.getGridOption('suppressExcelExport')) subMenu.push('excelExport');
    if (subMenu.length === 0) return null;
    return {
        name: api.getGridOption('localeText')?.export ?? 'Export',
        icon: getAgGridIconHtml('save'),
        subMenu,
    };
};

export const replaceNativeCsvExportMenuItems = (
    items: ContextMenuItemT[],
    api: GridApi,
): ContextMenuItemT[] =>
    items.flatMap((item) => {
        if (item === 'csvExport') {
            return api.getGridOption('suppressCsvExport')
                ? []
                : [getCsvExportMenuItem(api)];
        }
        if (item === 'export') {
            const exportMenuItem = getExportMenuItem(api);
            return exportMenuItem ? [exportMenuItem] : [];
        }
        return [item];
    });

/**
 * Make the context menu CSV export escape formula-like cells (CSV injection).
 * AG Grid has no option for it: a processCellCallback would bypass
 * its own rendering of formatted values, group paths and pivot headers, so the
 * entry exports the native CSV text and only escapes the dangerous fields.
 */
export const withFormulaSafeCsvExport =
    (getContextMenuItems?: GetContextMenuItems): GetContextMenuItems =>
    (params) => {
        const items =
            getContextMenuItems?.(params) ?? params.defaultItems ?? [];
        if (items instanceof Promise) {
            return items.then((resolvedItems) =>
                replaceNativeCsvExportMenuItems(resolvedItems, params.api),
            );
        }
        return replaceNativeCsvExportMenuItems(items, params.api);
    };
